<template>
    <div class="row">
        <div class="col text-start">
            <p>
                Both flexible drawdown income and guaranteed lifetime income (annuities)
                have a number of great features. You can model both together with us.
                <template v-if="displaySuppliers">
                  Then if you are looking to buy some guaranteed income now, <strong>you can do so via
                  Hargreaves Lansdown below</strong>. Our model uses current market rates.
                  They will ask you further questions on the exact type of annuity you want,
                  where you live, your lifestyle and health to get final rates for you.
                </template>
            </p>
            <p>
                Below the income, pots, and scenarios show the outcome of your plan assuming
                you buy an income with no increases, no spouses benefits and no guarantee for 
                how long it will be paid (lots of other types are available). To change the age at
                which you want to add this or the percentage of your non protected pot you want to
                use, move the sliders below and click <strong>Update Plan</strong> to see the effect.
            </p>
        </div>
    </div>

    <div class="row mt-3">
        <div class="col">
            <p class="text-start mb-0">

            </p>
        </div>
    </div>

    <div class="row mb-4">
        <div class="col-xxl-6 text-start">
            <label for="customRange3" class="form-label">
                Age to add some guaranteed income from:
                <span class="fw-bolder">{{ ageText }}</span>
            </label>
            <div class="d-flex justify-content-between guiide-range-slider">
                <div class="pe-3">
                    {{ minPurchaseAge }}
                </div>

                <input type="range"
                       class="form-range"
                       v-model="ageModel"
                       :min="minPurchaseAge"
                       :max="maxPurchaseAge"
                       step="1" />

                <div class="ps-3">
                    {{ maxPurchaseAge }}
                </div>
            </div>
        </div>
    </div>

    <div class="row mb-3">
        <div class="col-xxl-6 text-start">
            <label for="customRange3" class="form-label">
                Percentage of your non protected pension pot used to buy it:
                <span class="fw-bolder">{{ amountText }}</span>
            </label>
            <div class="d-flex justify-content-between guiide-range-slider">
                <div class="pe-3">
                    0
                </div>

                <input type="range"
                       class="form-range"
                       v-model="amountModel"
                       min="0"
                       :max="80"
                       step="5" />

                <div class="ps-3">80</div>
            </div>

            <button type="button"
                    class="btn btn-primary mt-4"
                    :disabled="inTransaction"
                    @click="onUpdateClicked()">
                Update Plan
                <i class="bi" :class="updateButtonIcon"></i>
            </button>
        </div>
    </div>

    <template v-if="isCurrentlySubscribedToGI">
        <div class="row" v-if="showMinimumPurchaseWarning">
            <div class="col text-start">
                <p class="fw-bold">
                    Please note that there is a minimum purchase amount of
                    {{ minPurchaseAmountFormatted }}.
                </p>
            </div>
        </div>
        <div class="row">
            <div class="col text-start">
                <p>
                    You have included a guaranteed income purchase in your plan.
                    {{ guaranteedIncomeSubscriptionText }}
                </p>
                <p>Click <i>Remove</i> to go back to just using non guaranteed drawdown income.</p>
            </div>
        </div>
        <div class="row" :class="{ 'mb-4': !displaySuppliers}">
            <div class="col">
                <button type="button"
                        class="btn btn-primary btn-sm float-start"
                        :disabled="inTransaction"
                        @click="onUnsubscribeGuaranteedIncomeClicked()">
                    <span class="d-none d-sm-inline-block me-2">Remove</span>
                    <i class="bi bi-dash-circle"></i>
                </button>
            </div>
        </div>
    </template>

    <AnnuityProvidersList v-if="displaySuppliers"></AnnuityProvidersList>

    <div class="container-fluid border border-dark rounded p-3 mb-4">
        <div class="row">
            <div class="col-lg-6">
                <income-chart />
            </div>
            <div class="col-lg-6">
                <pots-chart />
            </div>
        </div>
        <div class="row">
            <div class="col">
                <scenario :scenario="ScenarioType.AccordingToPlan"
                          number="1"
                          :showBorder="false" />
            </div>
        </div>
    </div>

    <div class="row">
        <div class="col-sm-6">
            <scenario :scenario="ScenarioType.LowerGrowth"
                      number="2"
                      :showGraphButton="false"
                      @show-graphs="onShowGraphs" />
        </div>
        <div class="col-sm-6">
            <scenario :scenario="ScenarioType.LiveThreeYearsLonger"
                      number="3"
                      :showGraphButton="false"
                      @show-graphs="onShowGraphs" />
        </div>
    </div>
    <div class="row mb-2">
        <div class="col-sm-6">
            <scenario :scenario="ScenarioType.PotsFallTwentyPercent"
                      number="4"
                      :showGraphButton="false"
                      @show-graphs="onShowGraphs" />
        </div>
        <div class="col-sm-6">
            <scenario :scenario="ScenarioType.NeedExtraMoneyForLastTwoYears"
                      number="5"
                      :showGraphButton="false"
                      @show-graphs="onShowGraphs" />
        </div>
    </div>

    <div v-if="displaySuppliers" class="row">
        <div class="col text-start">
            <p>
                <small>
                    The calculations above are based on illustrative figures. If you want to buy some guaranteed income you will need to get an actual 
                    quote. This quote will depend on a number of factors such as interest rates on that day and factors that are specific to you such 
                    as the annuity type you want, your age, health, postcode and lifestyle.
                </small>
            </p>
            <p>
                <small>
                    Disclaimer - We do not take any referral fees from any partners above. Some partners may license our tools for their own use, so we 
                    do have commercial relationships. You can purchase an annuity on your own or get independent financial advice to find the most 
                    appropriate. You can do this via our Advisers page, or go to <a href="https://www.moneyhelper.org.uk" target="_blank">moneyhelper.org.uk</a> if 
                    you want more help on finding an adviser.
                </small>
            </p>
        </div>
    </div>

    <modal ref="graphModal"
           :title="graphModalTitle"
           idKey="graphModal"
           key="graphModal"
           :isWide="true">
        <div class="row">
            <div class="col-6">
                <income-chart />
            </div>
            <div class="col-6">
                <pots-chart />
            </div>
        </div>
    </modal>
</template>

<script lang="ts">
import { defineComponent, ref } from "vue";
import Scenario from "@/guiide/components/scenarios/Scenario.vue";
import ScenarioType from "@/guiide/ts/scenarioType";
import IncomeChart from "@/guiide/components/charts/IncomeChart.vue";
import PotsChart from "@/guiide/components/charts/PotsChart.vue";
import AnnuityProvidersList from "@/guiide/components/common/AnnuityProvidersList.vue";
import Modal from "@/guiide/components/common/Modal.vue";
import Temporal from "@/shared/ts/temporal";
import ScenarioInfo from "@/guiide/ts/scenarioInfo";
import { app } from "@/guiide/ts/app";
import { GuaranteedIncome } from "@/guiide/ts/guaranteedIncome";
import { formatter } from "@/shared/ts/formatter";

export default defineComponent({
  name: "GuaranteedIncomeModeller",
  components: {
    IncomeChart,
    PotsChart,
    Scenario,
    AnnuityProvidersList,
    Modal
  },
  props: {
    showPageNav: { type: Boolean, default: true },
    headerText: {
      type: String,
      default:
        "Below is a summary of everything you have entered to put towards retirement and you can see here if the plan you have made remains on track."
    }
  },
  setup() {
    const minPurchaseAge = ref(60);
    const ageModel = ref(65);
    const amountModel = ref(15);
    const graphModal = ref(null);
    const notifyWhenAvailable = ref(null);

    return {
      minPurchaseAge,
      ageModel,
      amountModel,
      graphModal,
      notifyWhenAvailable
    };
  },
  data() {
    return {
      graphModalTitle: "Scenario graphs",
      minPurchaseAmount: 10000,
      maxPurchaseAge: 90,
      inTransaction: false,
      ScenarioType
    };
  },
  mounted() {
    this.minPurchaseAge = this.calcMinPurchaseAge();
    this.updateModelValues();
  },
  watch: {
    "$store.state.retiree"() {
      this.minPurchaseAge = this.calcMinPurchaseAge();
      this.updateModelValues();
    }
  },
  computed: {
    updateButtonIcon: function(): string {
      return this.isCurrentlySubscribedToGI
        ? "bi-pencil-fill"
        : "bi-plus-circle";
    },
    ageText: function(): string {
      return `${this.ageModel}`;
    },
    amountText: function(): string {
      return `${this.amountModel}%`;
    },
    isCurrentlySubscribedToGI: function(): boolean {
      return this.$store.state.retiree?.guaranteedIncome.subscribed ?? false;
    },
    guaranteedIncomeSubscriptionText: function(): string {
      if (!this.isCurrentlySubscribedToGI) {
        return "";
      }

      const guaranteedIncome: GuaranteedIncome = this.$store.state.retiree
        .guaranteedIncome;
      const allocationPercentage = guaranteedIncome.allocation;
      const allocationAmount = formatter.formatCurrency(
        guaranteedIncome.allocationAmount
      );
      const annualIncome = formatter.formatCurrency(
        guaranteedIncome.annualIncome
      );
      return (
        `You plan to allocate ${allocationPercentage}% of non protected funds at age ${guaranteedIncome.ageAtSubscription}, which is ${allocationAmount}. This may provide an estimated guaranteed` +
        ` income from that age of ${annualIncome}.`
      );
    },
    showMinimumPurchaseWarning: function(): boolean {
      if (!this.isCurrentlySubscribedToGI) {
        return false;
      }

      const guaranteedIncome: GuaranteedIncome = this.$store.state.retiree
        .guaranteedIncome;
      return guaranteedIncome.allocationAmount < this.minPurchaseAmount;
    },
    minPurchaseAmountFormatted: function(): string {
      return formatter.formatCurrency(this.minPurchaseAmount);
    },
    displaySuppliers: function(): boolean {
      return !this.$store.state.configuration.guaranteedIncomeSuppliersHidden;
    }
  },
  methods: {
    calcMinPurchaseAge: function(): number {
      if (this.$store.state.retiree == null) {
        const defaultMinPurchaseAge = 65;
        return defaultMinPurchaseAge;
      }

      const currentAge = Temporal.calcAge(
        new Date(this.$store.state.retiree.dateOfBirth)
      );

      if (this.$store.state.retiree.alreadyDrawingPension) {
        return currentAge;
      }

      const retirementAge = Temporal.calcAgeAtDate(
        new Date(this.$store.state.retiree.dateOfBirth),
        new Date(this.$store.state.retiree.retirementDate)
      );

      const ageToUse = currentAge > retirementAge ? currentAge : retirementAge;

      return ageToUse < this.maxPurchaseAge ? ageToUse : this.maxPurchaseAge;
    },
    updateModelValues() {
      const setDefaults = this.$store.state.retiree == null;
      let age = 0;
      let allocation = 0;
      let notify = false;

      if (setDefaults) {
        age = this.minPurchaseAge;
        allocation = 0;
        notify = false;
      } else {
        if (this.$store.state.retiree.guaranteedIncome.subscribed) {
          age = this.$store.state.retiree.guaranteedIncome.ageAtSubscription;
          allocation = this.$store.state.retiree.guaranteedIncome.allocation;
        } else {
          age = this.minPurchaseAge;
          allocation = 0;
        }
        notify = this.$store.state.retiree.guaranteedIncome.notifyWhenAvailable;
      }

      this.ageModel = age;
      this.amountModel = allocation;
    },
    onShowGraphs(scenarioType: ScenarioType) {
      this.graphModalTitle = ScenarioInfo.getName(scenarioType);
      this.graphModal.showModal();
    },
    async onUpdateClicked() {
      this.inTransaction = true;

      if (this.amountModel == 0) {
        if (this.isCurrentlySubscribedToGI) {
          await app.deleteGuaranteedIncome();
        }
        this.inTransaction = false;
        return;
      }

      if (this.isCurrentlySubscribedToGI) {
        await app.updateGuaranteedIncome(this.ageModel, this.amountModel);
      } else {
        await app.applyGuaranteedIncome(
          this.ageModel,
          this.amountModel,
          "GIModeller"
        );
      }

      this.inTransaction = false;
    },
    async onUnsubscribeGuaranteedIncomeClicked() {
      this.inTransaction = true;
      await app.deleteGuaranteedIncome();
      this.inTransaction = false;
    }
  }
});
</script>

<style>
.guiide-range-slider {
  border: 1px solid #ccc;
  padding: 14px;
  border-radius: 50px;
}
</style>
